'use client'
import { useRef, type FC } from 'react'
import styled from 'styled-components'
import { useInView } from 'motion/react'
import { innerGrid, mq, defaultGrid, vw, font20 } from '@/styles'
import type { TypeCmsMedium } from '@/schema'
import { useTranslator,  } from '@/hooks'
import { Img } from '../base/Img'

export interface TypeHomeGrid {
  data: TypeCmsMedium[]
}

const ImgWrapper = ({ item, idx }) => {
	const ref = useRef<HTMLDivElement>(null)
	const isInView = useInView(ref, { margin: '0px', once: true })

  return (
    <div ref={ref} className={`img-wrapper${isInView ? ' view' : ''}`} >
      <Img media={item?.image} lazy={idx > 2}/>
    </div>
  )
}

const Grid = (data) => {
  if(!data.data) return null

  return data.data.map((item, idx) => {
    return <ImgWrapper key={idx} item={item} idx={idx}/>
  })
}

export const SectionHomeGrid: FC<TypeHomeGrid> = ({ data }) => {
  const { t } = useTranslator()

  return (
    <Section>
      <h2 className='srt-only'>{t('project.home.welcome')}</h2>
      <article>
        <Grid data={data}/>
      </article>
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  grid-column: 1 / span 6;

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
  }

  h2 {
    ${font20(true)}
    text-transform: uppercase;
    margin: 0 auto ${vw(30, 'mobile')};

    ${mq.greaterThan('tablet')} {
      ${font20(false)}
      margin: 0 auto ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin: 0 auto 30px;
    }
  }

  article {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;

    ${mq.greaterThan('tablet')} {
      ${innerGrid(defaultGrid.tablet)}
      grid-column: 1 / span 12;
      row-gap: 30px;
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(defaultGrid.desktop)}
    }
  }

  picture {
    overflow: hidden;

    img {
      height: 100%;
    }
  }

  img {
    object-fit: cover;
  }

  .img-wrapper {
    opacity: 0;
    transform: translateY(10px);
    transition: 300ms opacity ease, 300ms transform ease ;

    &.view {
      opacity: 1;
      transform: translateY(0);
    }

    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(4),
    &:nth-of-type(5),
    &:nth-of-type(6),
    &:nth-of-type(7),
    &:nth-of-type(8),
    &:nth-of-type(9) {
      width: calc(50% - 1px);

      ${mq.greaterThan('tablet')} {
        width: 100%;
      }
    }

    &:nth-of-type(10),
    &:nth-of-type(11),
    &:nth-of-type(12),
    &:nth-of-type(15),
    &:nth-of-type(16),
    &:nth-of-type(17) {
      width: calc(${100 / 3}% - ${4 / 3}px);
      ${mq.greaterThan('tablet')} {
        width: 100%;
      }
    }

    &:nth-of-type(1),
    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(8),
    &:nth-of-type(9),
    &:nth-of-type(13),
    &:nth-of-type(14),
    &:nth-of-type(15),
    &:nth-of-type(16),
    &:nth-of-type(17) {
      ${mq.greaterThan('tablet')} {
        grid-row: auto / span 2;
      }

      > img,
      .placeholder,
      picture,
      figure {
        height: ${vw(200, 'mobile')};

        ${mq.greaterThan('tablet')} {
          height: ${vw(600, 'desktop')};
          width: 100%;
        }

        ${mq.greaterThan('desktop')} {
          height: 600px;
        }
      }
    }

    &:nth-of-type(4),
    &:nth-of-type(5),
    &:nth-of-type(6),
    &:nth-of-type(7),
    &:nth-of-type(10),
    &:nth-of-type(11),
    &:nth-of-type(12) {
      ${mq.greaterThan('tablet')} {
        grid-row: auto / span 1;
      }

      > img,
      picture,
      .placeholder,
      figure {
        height: ${vw(150, 'mobile')};

        ${mq.greaterThan('tablet')} {
          height: ${vw(285, 'desktop')};
          width: 100%;
        }

        ${mq.greaterThan('desktop')} {
          height: 285px;
        }
      }
    }

    &:nth-of-type(1) {
      width: 100%;

      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 8;
      }

      > img,
      picture,
      .placeholder,
      figure {
        width: 100%;
      }
    }

    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(4),
    &:nth-of-type(5),
    &:nth-of-type(6),
    &:nth-of-type(7),
    &:nth-of-type(10),
    &:nth-of-type(11),
    &:nth-of-type(12),
    &:nth-of-type(13),
    &:nth-of-type(15),
    &:nth-of-type(16),
    &:nth-of-type(17) {
      ${mq.greaterThan('tablet')} {
        grid-column: auto / span 4;
      }
    }

    &:nth-of-type(8),
    &:nth-of-type(9) {
      ${mq.greaterThan('tablet')} {
        grid-column: auto / span 6;
      }
    }

    &:nth-of-type(13) {
      width: calc(${100/3}% - 1px);

      ${mq.greaterThan('tablet')} {
        width: 100%;
      }

      > img,
      picture,
      .placeholder,
      figure {
        width: 100%;
      }
    }

    &:nth-of-type(14) {
      width: calc(${100/3*2}% - 1px);

      ${mq.greaterThan('tablet')} {
        grid-column: auto / span 8;
        width: 100%;
      }

      picture,
      .placeholder,
      figure {
        width: 100%;
      }
    }
  }


  .placeholder {
    background-color: var(--color-grey);
  }
`

